import { FC, useState } from 'react';
import MediaPreview from 'app/components/compoundMediaPreview';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons/faArrowDownToLine';
import { RunningUpload } from 'types/RunningUpload';
import { RunningUploadStatus } from 'constants/runningUpload';
import ProgressIndicator from 'app/components/ProgressIndicator';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Checkbox } from '@kontentino/ui';

type Props = {
  referenceImages: string[];
  resultImages: string[];
  runningUploads: RunningUpload[];
  onReferenceImagesChange: (urls: string[]) => void;
  onRemoveResultImage?: (imageUrl: string) => void;
  onSelectedImagesChange?: (selectedImages: string[]) => void;
};

const AiImageSuggestionsGallery: FC<Props> = ({
  referenceImages,
  resultImages,
  runningUploads,
  onReferenceImagesChange,
  onRemoveResultImage,
  onSelectedImagesChange,
}) => {
  const { t } = useTranslation();
  const [selectedImages, setSelectedImages] = useState<string[]>([]);

  const handleImageSelect = (imageUrl: string) => {
    setSelectedImages((prev) => {
      const newSelection = prev.includes(imageUrl)
        ? prev.filter((url) => url !== imageUrl)
        : [...prev, imageUrl];

      onSelectedImagesChange?.(newSelection);
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    setSelectedImages((prev) => {
      const newSelection =
        prev.length === resultImages.length ? [] : resultImages;
      onSelectedImagesChange?.(newSelection);
      return newSelection;
    });
  };

  const allSelected =
    resultImages.length > 0 && selectedImages.length === resultImages.length;

  return (
    <div className="tw-w-full" data-cy="ai-image-suggestions_gallery">
      {resultImages.length > 0 && (
        <div
          className="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-rounded-md tw-bg-grayscale-10 tw-p-3"
          data-cy="ai-image-suggestions_gallery_selection-bar"
        >
          <div className="tw-flex tw-items-center tw-gap-3">
            <Checkbox
              checked={allSelected}
              onChange={handleSelectAll}
              size="small"
              data-cy="ai-image-suggestions_gallery_select-all"
            />
            <span className="tw-text-base tw-text-grayscale-180">
              {selectedImages.length > 0
                ? `${selectedImages.length} of ${resultImages.length} selected`
                : `Select images to use in post or keep tweaking them until they're just right`}
            </span>
          </div>
        </div>
      )}

      <div
        className="tw-columns-2 tw-gap-2 tw-p-2"
        data-cy="ai-image-suggestions_gallery_grid"
      >
        {runningUploads.map((upload) => (
          <MediaPreview.Root
            key={upload.id}
            className="tw-relative tw-mb-2 tw-h-auto tw-w-full tw-break-inside-avoid tw-overflow-hidden tw-rounded-sm"
          >
            <img
              src={upload.base64Preview}
              alt={t('aiImageSuggestions.gallery.uploading')}
              className="tw-h-auto tw-w-full tw-rounded-sm"
            />
            <MediaPreview.Overlay />

            {upload.status === RunningUploadStatus.Uploading && (
              <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-sm tw-bg-grayscale-180/75">
                {typeof upload.progress === 'number' && (
                  <div className="tw-mb-2">
                    <ProgressIndicator
                      progress={upload.progress}
                      color="white"
                    />
                  </div>
                )}
                <span className="tw-text-sm tw-font-medium tw-text-white">
                  {t('uploading')}&hellip;
                </span>
              </div>
            )}

            {upload.status === RunningUploadStatus.Failed && (
              <div className="tw-bg-red-500/75 tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-sm">
                <span className="tw-text-sm tw-font-medium tw-text-white">
                  {t('upload_failed')}
                </span>
                {upload.error && (
                  <span className="tw-mt-1 tw-text-xs tw-text-white">
                    {upload.error}
                  </span>
                )}
              </div>
            )}

            {upload.status === RunningUploadStatus.Pending && (
              <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-sm tw-bg-grayscale-180/75">
                <span className="tw-text-sm tw-font-medium tw-text-white">
                  {t('pending')}&hellip;
                </span>
              </div>
            )}

            {upload.status === RunningUploadStatus.Processing && (
              <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-sm tw-bg-grayscale-180/75">
                <ActivityIndicator className="tw-h-6 tw-w-6" />
                <span className="tw-mt-2 tw-text-sm tw-font-medium tw-text-white">
                  {t('processing')}&hellip;
                </span>
              </div>
            )}

            <MediaPreview.Actions
              side="bottom"
              className="tw-right-1 tw-opacity-100"
            >
              <MediaPreview.ActionButton
                actionType="remove"
                onClick={() => {
                  upload.cancelHandler?.cancel?.();
                }}
              />
            </MediaPreview.Actions>
          </MediaPreview.Root>
        ))}
        {referenceImages.map((url, index) => (
          <MediaPreview.Root
            key={`reference-${url}-${index}`}
            className="tw-mb-2 tw-h-auto tw-w-full tw-break-inside-avoid tw-overflow-hidden tw-rounded-sm"
          >
            <img
              src={url}
              alt={t('aiImageSuggestions.gallery.referenceContent')}
              className="tw-h-auto tw-w-full"
            />
            <MediaPreview.Overlay />
            <MediaPreview.Actions side="bottom">
              <MediaPreview.ActionButton
                actionType="remove"
                onClick={() => {
                  const newUrls = referenceImages.filter((_, i) => i !== index);
                  onReferenceImagesChange(newUrls);
                }}
              />
            </MediaPreview.Actions>
          </MediaPreview.Root>
        ))}

        {resultImages.map((url, index) => {
          const isSelected = selectedImages.includes(url);
          const orderIndex = isSelected
            ? selectedImages.indexOf(url) + 1
            : undefined;

          return (
            <MediaPreview.Root
              key={`result-${url}-${index}`}
              className="tw-group tw-mb-2 tw-h-auto tw-w-full tw-break-inside-avoid tw-overflow-hidden tw-rounded-sm"
              isSelected={isSelected}
              orderIndex={orderIndex}
              onClick={() => handleImageSelect(url)}
            >
              <img
                src={url}
                alt={t('aiImageSuggestions.gallery.generatedContent')}
                className="tw-h-auto tw-w-full"
              />
              <MediaPreview.Overlay />

              <div className="tw-absolute tw-left-1 tw-top-1">
                {isSelected ? (
                  <div className="tw-flex tw-min-h-[1rem] tw-min-w-[1rem] tw-items-center tw-justify-center tw-rounded-sm tw-bg-primary-100 tw-px-1 tw-text-xs tw-font-semibold tw-text-white">
                    {orderIndex}
                  </div>
                ) : (
                  <div className="tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100">
                    <Checkbox
                      size="small"
                      checked={false}
                      onChange={() => handleImageSelect(url)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
              </div>

              {isSelected && (
                <div className="tw-absolute tw-left-0 tw-top-0 tw-h-full tw-w-full tw-rounded-md tw-border-[2px] tw-border-primary-100" />
              )}

              <MediaPreview.Actions
                side="bottom"
                className="tw-right-1 tw-opacity-100"
              >
                <div className="tw-flex tw-overflow-hidden tw-rounded-md">
                  <div className="tw-flex tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100">
                    {onRemoveResultImage && (
                      <MediaPreview.ActionButton
                        actionType="custom"
                        customIcon={faXmark}
                        customTooltip={t(
                          'aiImageSuggestions.gallery.removeImage',
                        )}
                        iconSize="lg"
                        variant="secondary"
                        className="tw-rounded-none"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveResultImage(url);
                        }}
                      />
                    )}
                    <MediaPreview.ActionButton
                      actionType="custom"
                      customIcon={faArrowDownToLine}
                      customTooltip={t(
                        'aiImageSuggestions.gallery.downloadImage',
                      )}
                      iconSize="lg"
                      variant="secondary"
                      className="tw-rounded-none"
                      onClick={(e) => {
                        e.stopPropagation();
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `ai-image-gen-${Date.now()
                          .toString()
                          .slice(-4)}.png`;
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                      }}
                    />
                  </div>
                </div>
              </MediaPreview.Actions>
            </MediaPreview.Root>
          );
        })}
      </div>
    </div>
  );
};

export default AiImageSuggestionsGallery;
